import { useEntitlementError } from '@context/EntitlementContext';
import { useInviteWorkspaceMember } from '@hooks/postQueries';
import useWorkspace from '@hooks/useWorkspace';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

const frontendURL = import.meta.env.VITE_FRONTEND_URL;

// Transition component for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const InviteMemberDialogCollaborators = ({ open, onClose, prefilledEmail = '', onSuccess }) => {
  const { activeWorkspace } = useWorkspace();
  const { handleApiError } = useEntitlementError();
  const inviteMutation = useInviteWorkspaceMember();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showSignupHint, setShowSignupHint] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const signupUrl = `https://dashboard.qualz.ai/signup`;

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user_email: prefilledEmail,
      role: 'member',
      permissions: ['view'],
      message: '',
    },
  });

  React.useEffect(() => {
    if (prefilledEmail) {
      setValue('user_email', prefilledEmail);
    }
  }, [prefilledEmail, setValue]);

  // Send invitees to the dedicated accept-invitation page so the token is
  // consumed via the same flow as in-app notifications. The workspace detail
  // page does not handle the `?token=` query for non-members.
  const returnUrl = `${frontendURL}/workspaces/${activeWorkspace?.id}/accept`;

  const onSubmit = (values) => {
    if (!activeWorkspace?.id) {
      toast.error('No active workspace found');
      return;
    }

    // Use permissions from form or set default based on role
    const permissions =
      values.permissions && values.permissions.length > 0
        ? values.permissions
        : values.role === 'admin'
          ? ['view', 'edit', 'admin']
          : ['view'];

    inviteMutation.mutate(
      {
        workspaceId: activeWorkspace.id,
        payload: {
          user_email: values.user_email,
          role: values.role,
          permissions: permissions,
          message: values.message || '',
          return_url: returnUrl,
        },
      },
      {
        onSuccess: () => {
          onClose();
          reset();
          if (onSuccess) {
            onSuccess();
          }
        },

        onError: (err) => {
          // Check for entitlement/cap limit errors first (403 MAX_SEATS_REACHED)
          if (handleApiError(err)) {
            onClose();
            return;
          }

          const backendMessage =
            err?.response?.data?.detail?.message ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.detail ||
            err?.message ||
            '';
          if (backendMessage === 'User is not registered') {
            setShowSignupHint(true);
            setErrorMessage('');
          } else {
            setShowSignupHint(false);
            setErrorMessage(backendMessage || 'Failed to invite member. Please try again.');
          }
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    reset();
    setErrorMessage('');
    setShowSignupHint(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(signupUrl);
    setCopied(true);

    // reset after 2 seconds
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: (theme) => theme.shadows[10],
          overflow: 'hidden',
        },
      }}
      TransitionComponent={Transition}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pt: 3,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Add to Workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Invite this person to join your workspace to collaborate
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <Alert severity="info">
        You’re granting workspace access only. Study access will remain restricted.
      </Alert>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 3, px: 3, overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <FormLabel>Email Address</FormLabel>
              <TextField
                type="email"
                {...register('user_email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={Boolean(errors.user_email)}
                helperText={errors.user_email?.message}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused': {
                      boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              />
              {showSignupHint && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="body2">
                      User with that email is not found. Please ask them to sign up and create a
                      qualz.ai account at{' '}
                      <Typography component="span" sx={{ textDecoration: 'underline' }}>
                        {signupUrl}
                      </Typography>{' '}
                      <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                        <IconButton onClick={handleCopy} sx={{ padding: 0.25 }}>
                          {copied ? (
                            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Typography>

                    <Box>
                      <Typography variant="body2">
                        After they create an account, you can add them to this workspace.
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              )}

              {errorMessage && (
                <Alert sx={{ mt: 1 }} severity="error">
                  {errorMessage}
                </Alert>
              )}
            </FormControl>

            <Controller
              name="role"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <FormLabel id="role-label">Role</FormLabel>
                  <Select
                    {...field}
                    startAdornment={
                      <InputAdornment position="start" sx={{ mr: 1, ml: -0.5 }}>
                        <PersonIcon color="action" fontSize="small" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused': {
                        boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    <MenuItem value="member">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Member" size="small" color="default" />
                        <Typography variant="body2">Can create and manage studies</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="admin">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Admin" size="small" color="primary" />
                        <Typography variant="body2">
                          Can manage members and workspace settings
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="guest">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Guest" size="small" color="info" />
                        <Typography variant="body2">Can view studies</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>Choose the appropriate role for this team member</FormHelperText>
                </FormControl>
              )}
            />

            <TextField
              {...register('message')}
              fullWidth
              multiline
              rows={3}
              placeholder="Add a personal message to the invitation..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused': {
                    boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleClose} disabled={inviteMutation.isPending} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={inviteMutation.isPending}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            }}
            startIcon={
              inviteMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
          >
            {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteMemberDialogCollaborators;
